<template>
  <section ref="root" :class="[$style['stage'], $style[`stage--${mode}`]]" aria-labelledby="stage-title">
    <HlsPlayer :class="$style['stage__video']" src="/home/2026/playlist.m3u8" poster="/home/2026/poster.webp" aria-hidden="true" />

    <div ref="mask" :class="$style['stage__mask']">
      <h1 id="stage-title" :class="$style['stage__title']">
        <span>{{ lines[0] }}</span>
        <span>{{ lines[1] }}<span ref="letter">{{ lines[2] }}</span></span>
      </h1>
    </div>

    <div :class="[$style['stage__hint'], hintShown && $style['stage__hint--shown'], hintHidden && $style['stage__hint--hidden']]" aria-hidden="true">
      Scroll<i />
    </div>

    <div ref="after" :class="$style['stage__after']">
      <p :class="$style['stage__kicker']">{{ kicker }}</p>
      <!-- The show's own logo; the title is already the heading above, so the image itself is silent -->
      <img v-if="logo" :src="logo.src" :width="logo.width" :height="logo.height" alt="" :class="$style['stage__logo']">
      <p v-if="when" :class="[$style['stage__when'], logo && $style['stage__when--small']]">{{ when }}</p>
      <p :class="$style['stage__where']">{{ where }}</p>
      <Button variant="primary" size="lg" to="#avond" :class="$style['stage__cta']">Kies je voorstelling</Button>
    </div>

    <div ref="curtain" :class="$style['curtain']" aria-hidden="true" @click="openCurtain">
      <div v-for="side in ['left', 'right']" :key="side" :class="[$style['curtain__drape'], $style[`curtain__drape--${side}`]]">
        <div :class="$style['curtain__inner']">
          <div v-for="n in FOLDS" :key="n" :class="$style['curtain__fold']" />
        </div>
      </div>
      <div :class="$style['curtain__valance']" />
      <div :class="$style['curtain__logo']"><Logo /></div>
    </div>
  </section>
</template>
<script setup lang="ts">
const props = defineProps<{
  title: string
  kicker: string
  when?: string
  where: string
  logo?: { src: string, width: number, height: number }
}>()
const emit = defineEmits<{ state: ['closed' | 'open'] }>()

// A traveller curtain of loose folds: the leading edge is pulled first, the rest follows and bunches up
// at the side, the hem trails and swings back. It stays gathered at the sides until you scroll into the
// letters. Without JavaScript a simpler CSS version opens by itself; with reduced motion there is none.
const FOLDS = 14
const $style = useCssModule()
const { prefersReducedMotion, register, scheduleRefresh } = useStageMotion()

const lines = computed(() => {
  const words = props.title.split(' ')
  const last = words.pop() ?? ''
  return [words.join(' '), last.slice(0, -1), last.slice(-1)]
})

const mode = ref<'css' | 'js' | 'reduced'>('css')
const hintShown = ref(false)
const hintHidden = ref(false)
const root = ref<HTMLElement>()
const mask = ref<HTMLElement>()
const letter = ref<HTMLElement>()
const after = ref<HTMLElement>()
const curtain = ref<HTMLElement>()

let context: ReturnType<ReturnType<typeof register>['gsap']['context']> | undefined
let openTimer: ReturnType<typeof setTimeout> | undefined
let curtainOpen = false
const cleanups: Array<() => void> = []

const gathered = () => Math.max(window.innerWidth * 0.1, 40)

function eachDrape (fn: (folds: Array<HTMLElement>, slot: number, gatheredSlot: number) => void) {
  root.value?.querySelectorAll<HTMLElement>(`.${$style['curtain__inner']}`).forEach(inner => {
    inner.style.setProperty('--backing', `${gathered()}px`)
    const folds = Array.from(inner.querySelectorAll<HTMLElement>(`.${$style['curtain__fold']}`))
    fn(folds, inner.getBoundingClientRect().width / FOLDS, gathered() / FOLDS)
  })
}

function placeFolds (open: boolean) {
  const { gsap } = register()
  eachDrape((folds, slot, gatheredSlot) => {
    folds.forEach((fold, i) => gsap.set(fold, open
      ? { x: i * gatheredSlot, scaleX: gatheredSlot / slot, skewX: 0 }
      : { x: i * slot, scaleX: 1, skewX: 0 }))
  })
}

function openCurtain () {
  if (mode.value !== 'js' || curtainOpen || !context) return
  curtainOpen = true
  const { gsap } = register()
  context.add(() => {
    const timeline = gsap.timeline()
    timeline.to(`.${$style['curtain__logo']}`, { autoAlpha: 0, scale: 0.94, duration: 0.5, ease: 'power2.in' }, 0)
    eachDrape((folds, slot, gatheredSlot) => {
      folds.forEach((fold, i) => {
        const lead = (i + 1) / FOLDS
        const start = 0.2 + (FOLDS - 1 - i) * 0.05
        // Nearly the same lean for neighbours, so the folds sway together and no gaps open between them
        const lean = 4 * (0.6 + 0.4 * lead)
        timeline.to(fold, { x: i * gatheredSlot, scaleX: gatheredSlot / slot, duration: 2.2, ease: 'power3.inOut' }, start)
        timeline.to(fold, { skewX: lean, duration: 1, ease: 'sine.out' }, start)
        timeline.to(fold, { skewX: 0, duration: 1.8, ease: 'elastic.out(1, 0.4)' }, start + 1)
      })
    })
    timeline.add(() => emit('state', 'open'), 1.3)
    timeline.add(() => {
      hintShown.value = true
      if (curtain.value) curtain.value.style.pointerEvents = 'none'
    }, 2.2)
  })
}

// Zoom through the stem of the last letter. offsetLeft/offsetTop ignore transforms, so this also works mid-zoom.
function aimAtLetter () {
  if (!mask.value || !letter.value) return
  const { gsap } = register()
  const x = letter.value.offsetLeft + letter.value.offsetWidth * 0.3
  const y = letter.value.offsetTop + letter.value.offsetHeight * 0.5
  gsap.set(mask.value, { transformOrigin: `${x}px ${y}px` })
}

function buildScroll () {
  const { gsap } = register()
  aimAtLetter()
  gsap.timeline({
    scrollTrigger: { trigger: root.value, start: 'top top', end: '+=170%', scrub: 0.6, pin: true, refreshPriority: 3, invalidateOnRefresh: true, onRefresh: aimAtLetter }
  })
    .to(`.${$style['curtain__drape--left']}`, { x: () => -(gathered() + 60), duration: 0.3, ease: 'power2.in' }, 0)
    .to(`.${$style['curtain__drape--right']}`, { x: () => gathered() + 60, duration: 0.3, ease: 'power2.in' }, 0)
    .to(`.${$style['curtain__valance']}`, { yPercent: -115, duration: 0.3, ease: 'power2.in' }, 0)
    .to(mask.value!, { scale: 60, ease: 'power3.in', duration: 1 }, 0)
    .to(mask.value!, { autoAlpha: 0, duration: 0.12 }, 0.86)
    .fromTo(after.value!, { autoAlpha: 0, y: 60 }, { autoAlpha: 1, y: 0, duration: 0.25, ease: 'power2.out' }, 0.9)
}

function listen (target: Window, type: string, handler: EventListener, options?: AddEventListenerOptions) {
  target.addEventListener(type, handler, options)
  cleanups.push(() => target.removeEventListener(type, handler, options))
}

onMounted(async () => {
  if (prefersReducedMotion()) {
    mode.value = 'reduced'
    emit('state', 'open')
    // No moving video either: pause it as soon as the player starts it
    root.value?.addEventListener('play', event => (event.target as HTMLVideoElement).pause(), true)
    return
  }

  const { gsap } = register()
  mode.value = 'js'
  emit('state', 'closed')
  await nextTick()
  context = gsap.context(() => placeFolds(false), root.value)

  if (document.fonts?.ready) await document.fonts.ready
  context.add(buildScroll)
  scheduleRefresh()

  openTimer = setTimeout(openCurtain, 1000)
  for (const type of ['wheel', 'touchstart', 'keydown']) {
    listen(window, type, openCurtain, { once: true, passive: true })
  }
  listen(window, 'scroll', () => { hintHidden.value = window.scrollY > 30 }, { passive: true })
  listen(window, 'resize', () => { if (curtainOpen) placeFolds(true) })
})

onBeforeUnmount(() => {
  clearTimeout(openTimer)
  cleanups.forEach(cleanup => cleanup())
  context?.revert()
})
</script>
<style module>
.stage {
  position: relative;
  height: 100svh;
  overflow: hidden;
  isolation: isolate;
  background: var(--stage-black);
  color: var(--stage-white);
}

.stage__video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(1.35) contrast(1.15) brightness(1.1);
}

/* The trailer shows through the white letters. No will-change here: Chrome then keeps a stale raster of the
   60x scaled layer and paints the title screen black after scrolling into the next section and back up. */
.stage__mask {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: var(--stage-black);
  color: var(--stage-white);
  mix-blend-mode: multiply;
}

.stage__title {
  margin: 0;
  font-family: var(--font-family-heading);
  font-weight: var(--font-weight-bold);
  font-size: clamp(4.5rem, 19.5vw, 20rem);
  line-height: 0.8;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  text-align: center;
  color: var(--stage-white);
}

.stage__title > span {
  display: block;
}

.stage__hint {
  position: absolute;
  left: 50%;
  bottom: var(--spacing-xl);
  z-index: 2;
  display: grid;
  justify-items: center;
  gap: var(--spacing);
  transform: translateX(-50%);
  font-size: var(--font-size-small);
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--stage-soft);
  opacity: 0;
  transition: opacity 600ms;
}

.stage__hint--shown {
  opacity: 1;
}

.stage__hint--hidden {
  opacity: 0;
}

.stage__hint i {
  display: block;
  width: var(--stage-hairline);
  height: 2.5rem;
  background: linear-gradient(var(--stage-white), oklch(100% 0 0deg / 0%));
  transform-origin: top;
  animation: drip 1.8s ease-in-out infinite;
}

@keyframes drip {
  0% {
    transform: scaleY(0);
  }

  50% {
    transform: scaleY(1);
  }

  100% {
    transform: scaleY(1);
    opacity: 0;
  }
}

.stage__after {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: var(--spacing-l);
  padding: var(--stage-gutter);
  padding-bottom: clamp(2.5rem, 8vh, 5rem);
  background: linear-gradient(to top, oklch(0% 0 0deg / 80%), oklch(0% 0 0deg / 0%) 60%);
  opacity: 0;
  visibility: hidden;
}

.stage__kicker {
  margin: 0;
  font-size: var(--font-size-base);
  text-transform: uppercase;
  letter-spacing: 0.2em;
}

.stage__when {
  margin: 0;
  font-family: var(--font-family-heading);
  font-weight: var(--font-weight-bold);
  font-size: clamp(2.5rem, 1rem + 6vw, 7.5rem);
  line-height: 0.88;
  letter-spacing: -0.035em;
  text-transform: uppercase;
}

.stage__logo {
  display: block;
  width: clamp(16rem, 8rem + 32vw, 42rem);
  max-width: 100%;
  height: auto;
  margin-left: calc(-0.02 * clamp(16rem, 8rem + 32vw, 42rem));
}

/* Next to the show logo the dates step back */
.stage__when--small {
  font-size: clamp(1.75rem, 1rem + 2.6vw, 3.5rem);
}

.stage__where {
  font-size: clamp(1.125rem, 1rem + 0.6vw, 1.5rem);
  margin: 0 0 var(--spacing);
  color: var(--stage-soft);
}

.stage__cta {
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* ---------- The curtain ---------- */
.curtain {
  position: absolute;
  inset: 0;
  z-index: 5;
  overflow: hidden;
  cursor: pointer;
}

.curtain__drape {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50.5%;
}

.curtain__drape--left {
  left: 0;
}

.curtain__drape--right {
  right: 0;
  transform: scaleX(-1);
}

.curtain__inner {
  --folds: 14;

  position: absolute;
  inset: 0;
  transform-origin: 0 50%;
  background:
    linear-gradient(
      to bottom,
      oklch(0% 0 0deg / 45%),
      oklch(0% 0 0deg / 0%) 22%,
      oklch(0% 0 0deg / 0%) 72%,
      oklch(0% 0 0deg / 60%)
    ),
    repeating-linear-gradient(
      90deg,
      var(--velours-1) 0,
      var(--velours-2) 1.3rem,
      var(--velours-3) 2.4rem,
      var(--velours-2) 3.5rem,
      var(--velours-1) 4.8rem
    );
}

/* Each fold is twice as wide as its slot and tucks under the next one, like real pleats */
.curtain__fold {
  display: none;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: calc(200% / var(--folds));
  transform-origin: 0 0;
  background:
    linear-gradient(
      to bottom,
      oklch(0% 0 0deg / 45%),
      oklch(0% 0 0deg / 0%) 22%,
      oklch(0% 0 0deg / 0%) 70%,
      oklch(0% 0 0deg / 65%)
    ),
    linear-gradient(
      90deg,
      var(--velours-1),
      var(--velours-2) 20%,
      var(--velours-3) 48%,
      var(--velours-2) 76%,
      var(--velours-1)
    ) 0 0 / 50% 100% no-repeat,
    var(--velours-1);
  box-shadow: -0.75rem 0 1.25rem oklch(0% 0 0deg / 35%);
}

.curtain__fold:last-child {
  background-size: auto, 100% 100%;
  box-shadow: -0.75rem 0 1.25rem oklch(0% 0 0deg / 35%), 1.5rem 0 2.5rem oklch(0% 0 0deg / 55%);
}

.curtain__valance {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  height: clamp(4.5rem, 12vh, 8rem);
  background:
    linear-gradient(to bottom, oklch(0% 0 0deg / 35%), oklch(0% 0 0deg / 0%) 60%),
    repeating-linear-gradient(90deg, var(--velours-2) 0, var(--velours-3) 3rem, var(--velours-2) 6rem);
  border-top: 0.375rem solid var(--primary-color);
  mask: radial-gradient(3.2rem 1.6rem at 3rem 100%, transparent 98%, #000) 0 0 / 6rem 100% repeat-x;
}

.curtain__logo {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: grid;
  place-items: center;
}

.curtain__logo > * {
  width: min(26rem, 72vw);
  filter: drop-shadow(0 0.5rem 2rem oklch(0% 0 0deg / 60%));
}

/* GSAP drives the folds */
.stage--js .curtain__inner {
  background: none;
}

.stage--js .curtain__fold {
  display: block;
}

/* A velvet backing along the outer edge, so the sides stay covered while the curtain swings */
.stage--js .curtain__inner::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: -8rem;
  width: calc(8rem + var(--backing, 6rem));
  background:
    linear-gradient(
      to bottom,
      oklch(0% 0 0deg / 45%),
      oklch(0% 0 0deg / 0%) 22%,
      oklch(0% 0 0deg / 0%) 70%,
      oklch(0% 0 0deg / 65%)
    ),
    repeating-linear-gradient(
      90deg,
      var(--velours-1) 0,
      var(--velours-2) 0.6rem,
      var(--velours-3) 1.1rem,
      var(--velours-2) 1.6rem,
      var(--velours-1) 2.2rem
    );
}

/* Without JavaScript the curtain opens by itself */
.stage--css .curtain {
  animation: curtain-off 0s linear 3.6s forwards;
}

.stage--css .curtain__inner {
  animation: gather 1.9s var(--stage-ease) 1.6s forwards;
}

.stage--css .curtain__valance {
  animation: rise 1.1s var(--stage-ease) 2s forwards;
}

.stage--css .curtain__logo {
  animation: logo-off 0.5s ease-in 1.5s forwards;
}

@keyframes curtain-off {
  to {
    visibility: hidden;
    pointer-events: none;
  }
}

@keyframes gather {
  60% { transform: scaleX(0.2); }
  100% { transform: translateX(-24%) scaleX(0.2); }
}

@keyframes rise {
  to { transform: translateY(-115%); }
}

@keyframes logo-off {
  to {
    opacity: 0;
    transform: scale(0.94);
  }
}

/* With reduced motion: a calm page, the title over the trailer and the dates below it */
.stage--reduced {
  height: auto;
}

.stage--reduced .stage__mask {
  position: relative;
  height: 100svh;
}

.stage--reduced .stage__after {
  position: relative;
  min-height: 60svh;
  opacity: 1;
  visibility: visible;
}

.stage--reduced .stage__hint,
.stage--reduced .curtain {
  display: none;
}

@media (prefers-reduced-motion: reduce) {
  .curtain {
    display: none;
  }

  .stage__hint i {
    animation: none;
  }
}
</style>
